import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listSimulations, deleteSimulation, createSimulation, getSignalById, finalizeDraftSignal, deleteSignal } from '../../../utils/api';
import { Simulation } from './SimulationCard';
import CaseCard from './CaseCard';
import { MultiDocumentSignalWizard } from '../shared/MultiDocumentSignalWizard';
import SignalSummaryCard from '../shared/SignalSummaryCard';
import { FOSExplorerPage } from '../fos-explorer';
import { toast } from 'react-toastify';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import { useNavigation } from '../../../contexts/NavigationContext';
import IconWrapper from '../../IconWrapper';
import { FiUpload, FiSearch, FiPlay, FiPlus } from 'react-icons/fi';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import SegmentedNav from '../../ui/segmented-nav';

type WorkspaceTab = 'simulations' | 'explorer';
type SlaBucketKey = 'within3Days' | 'within8Weeks' | 'past8Weeks';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const createEmptyBuckets = (): Record<SlaBucketKey, Simulation[]> => ({
  within3Days: [],
  within8Weeks: [],
  past8Weeks: [],
});

const getBucketLabel = (bucket: SlaBucketKey) => {
  switch (bucket) {
    case 'within3Days':
      return 'Within 3 days';
    case 'within8Weeks':
      return 'Within 8 weeks';
    case 'past8Weeks':
      return 'Past 8 weeks';
    default:
      return bucket;
  }
};

const getSimulationAgeInDays = (dateString: string) => {
  const createdAt = new Date(dateString).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((now - createdAt) / MS_PER_DAY));
};

const getSlaBucket = (simulation: Simulation): SlaBucketKey => {
  const ageInDays = getSimulationAgeInDays(simulation.created_at);

  if (ageInDays <= 3) {
    return 'within3Days';
  }

  if (ageInDays <= 56) {
    return 'within8Weeks';
  }

  return 'past8Weeks';
};

const formatRelativeTime = (dateString: string) => {
  const updatedAt = new Date(dateString).getTime();
  const diffInHours = Math.max(0, Math.floor((Date.now() - updatedAt) / (1000 * 60 * 60)));

  if (diffInHours < 1) {
    return 'Active just now';
  }

  if (diffInHours < 24) {
    return `Active ${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `Active ${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 8) {
    return `Active ${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `Active ${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `Active ${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
};

const formatCaseLabel = (simulationId: string) => {
  const shortId = simulationId.replace(/-/g, '').slice(0, 8).toUpperCase();
  return `CG-${shortId}`;
};

const OmbudsmanWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateWithReferrer } = useNavigation();
  const { subscribe, unsubscribe, addMessageListener } = useWebSocket();
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [, setIsProcessing] = useState(false);
  const [createdSignal, setCreatedSignal] = useState<any>(null);
  const [isCreatingSimulation, setIsCreatingSimulation] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Get active tab from query params, default to 'simulations'
  const activeTab = (searchParams.get('tab') as WorkspaceTab | null) || 'simulations';

  // Get signal_id from query params for filtering
  const filterSignalId = searchParams.get('signal_id');

  // Handle tab change
  const handleTabChange = (tab: WorkspaceTab) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });
  };

  const fetchSimulations = async () => {
    try {
      setIsLoading(true);
      // Pass signal_id filter if present in query params
      const data = await listSimulations(filterSignalId || undefined);
      
      // Handle both array response and wrapped response
      const simulationsArray = Array.isArray(data) ? data : (data?.simulations || data?.data || []);
      
      setSimulations(simulationsArray);
    } catch (error) {
      console.error('Failed to fetch simulations:', error);
      toast.error('Failed to load simulations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSimulations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSignalId]);

  // Subscribe to WebSocket updates for the created signal
  useEffect(() => {
    if (!createdSignal?.id && !createdSignal?.signal_id) return;

    const signalId = createdSignal.id || createdSignal.signal_id;
    
    const handleMessage = (data: any) => {
      // Handle signal updates
      if (data.type === 'signal.updated' || data.type === 'signal.classified' || data.type === 'signal.progress') {
        const updateSignalId = data.signal_id || data.data?.signal_id || data.data?.id;
        if (updateSignalId === signalId) {
          console.log('Received real-time signal update:', data);
          
          // For classification updates, refetch the complete signal to get all classification details
          if (data.type === 'signal.classified') {
            getSignalById(signalId).then(updatedSignal => {
              setCreatedSignal(updatedSignal);
            }).catch(err => {
              console.error('Failed to fetch updated signal:', err);
            });
          } else if (data.type === 'signal.progress') {
            // For progress updates, update processing_status from the update
            const processingStatus = data.data?.processing_status || data.data?.status;
            if (processingStatus) {
              setCreatedSignal((prev: any) => prev ? { 
                ...prev, 
                processing_status: processingStatus 
              } : null);
            }
          } else {
            // For other updates, merge the data
            setCreatedSignal((prev: any) => prev ? { ...prev, ...data.data } : null);
          }
        }
      }
    };
    
    const cleanup = addMessageListener(handleMessage);
    
    return () => {
      unsubscribe('signal', signalId);
      cleanup();
    };
  }, [createdSignal?.id, createdSignal?.signal_id, subscribe, unsubscribe, addMessageListener]);

  const handleDelete = async (simulationId: string) => {
    try {
      await deleteSimulation(simulationId);
      setSimulations(prev => prev.filter(s => s.id !== simulationId));
      toast.success('Case deleted successfully');
    } catch (error) {
      console.error('Failed to delete case:', error);
      toast.error('Failed to delete case');
      throw error;
    }
  };

  const handleSimulationClick = (simulationId: string) => {
    navigateWithReferrer(`/v3/ombudsman/simulations/${simulationId}`, '/v3/ombudsman');
  };

  const handleWizardSuccess = async (signalId: string) => {
    try {
      setIsProcessing(true);

      // Fetch the created draft signal to get full details
      const fullSignal = await getSignalById(signalId);
      setCreatedSignal(fullSignal);
      
      toast.success('Draft Record created successfully');
      
      // Subscribe to signal updates for real-time classification status
      subscribe('signal', signalId);
      
      // Refresh simulations list in case new simulation was auto-created
      fetchSimulations();
    } catch (error) {
      console.error('Failed to fetch created signal:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load created signal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFinalizeDraft = async (signalId: string) => {
    try {
      setIsProcessing(true);
      await finalizeDraftSignal(signalId);
      
      // Fetch updated signal
      const updatedSignal = await getSignalById(signalId);
      setCreatedSignal(updatedSignal);
      
      toast.success('Feedback saved and processing started');
      
      // Subscribe to signal updates
      subscribe('signal', signalId);
    } catch (error) {
      console.error('Failed to finalize draft signal:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save feedback');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDiscardDraft = async (signalId: string) => {
    if (!window.confirm('Are you sure you want to discard this draft? This will delete the signal and cannot be undone.')) {
      return;
    }

    try {
      setIsProcessing(true);
      await deleteSignal(signalId);
      
      setCreatedSignal(null);
      toast.success('Draft discarded');
      
      // Refresh simulations list
      fetchSimulations();
    } catch (error) {
      console.error('Failed to discard draft signal:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to discard draft');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewSignal = (signalId: string) => {
    // Navigate to V4 records view
    navigateWithReferrer(`/v4/records/default/${signalId}`, '/v3/ombudsman');
  };

  const handleStartSimulation = async (signalId: string) => {
    try {
      setIsCreatingSimulation(true);
      
      const response = await createSimulation(signalId);
      
      if (response.id) {
        toast.success('Case guidance started');
        // Navigate to case detail page with referrer
        navigateWithReferrer(`/v3/ombudsman/simulations/${response.id}`, '/v3/ombudsman');
      } else {
        throw new Error('Case guidance creation failed');
      }
    } catch (error) {
      console.error('Failed to create case guidance:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start case guidance');
    } finally {
      setIsCreatingSimulation(false);
    }
  };

  const groupedSimulations = simulations.reduce<Record<SlaBucketKey, Simulation[]>>((groups, simulation) => {
    groups[getSlaBucket(simulation)].push(simulation);
    return groups;
  }, createEmptyBuckets());

  const sortedBuckets: SlaBucketKey[] = ['within3Days', 'within8Weeks', 'past8Weeks'];
  const totalCases = simulations.length;
  const urgentCases = groupedSimulations.past8Weeks.length;
  const activeCases = simulations.filter((simulation) => simulation.status !== 'completed').length;
  const processingCases = simulations.filter((simulation) => simulation.status === 'processing').length;

  return (
    <div className="flex h-full flex-col bg-[var(--go-spring)]">
      <div className="flex-1 overflow-auto px-4 py-4 md:px-6 md:py-5 xl:px-8 xl:py-6">
        <div className="flex w-full flex-col">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground">
                {activeTab === 'explorer' ? 'FOS Explorer' : 'My Cases'}
              </h1>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {activeTab === 'explorer'
                  ? 'Search precedent decisions and supporting evidence.'
                  : 'Case guidance for your active complaints.'}
              </p>
            </div>

            {activeTab === 'simulations' && (
              <Button size="lg" onClick={() => setIsWizardOpen(true)} className="gap-2 px-5">
                <IconWrapper icon={FiPlus} className="h-4 w-4" />
                New case
              </Button>
            )}
          </div>

          <SegmentedNav
            className="mb-6"
            ariaLabel="Case Guidance sections"
            activeKey={activeTab}
            items={[
              {
                key: 'simulations',
                label: 'Cases',
                onSelect: () => handleTabChange('simulations'),
                icon: <IconWrapper icon={FiPlay} className="h-4 w-4" />,
              },
              {
                key: 'explorer',
                label: 'FOS Explorer',
                onSelect: () => handleTabChange('explorer'),
                icon: <IconWrapper icon={FiSearch} className="h-4 w-4" />,
              },
            ]}
          />

          {/* Multi-Document Wizard */}
          <MultiDocumentSignalWizard
            open={isWizardOpen}
            onClose={() => setIsWizardOpen(false)}
            onSuccess={handleWizardSuccess}
            defaultRunSimulation={true}
          />

          {createdSignal && (
            <div className="mb-6">
              <SignalSummaryCard
                signal={{
                  id: createdSignal.id || createdSignal.signal_id,
                  source_id: createdSignal.source_id || '',
                  customer_name: createdSignal.customer_name || 'Unknown',
                  customer_email: createdSignal.customer_email,
                  customer_phone: createdSignal.customer_phone,
                  summary: createdSignal.summary || (createdSignal.verbatim ? createdSignal.verbatim.substring(0, 150) + '...' : 'No summary'),
                  verbatim: createdSignal.verbatim,
                  summary_detailed: createdSignal.summary_detailed,
                  channel: createdSignal.channel || 'unknown',
                  journey_stage: createdSignal.journey_stage,
                  severity_score: createdSignal.severity_score,
                  created_at: createdSignal.created_at || new Date().toISOString(),
                  processing_status: createdSignal.processing_status,
                  status: createdSignal.status,
                  complaint_detected: createdSignal.complaint_detected,
                  complaint_confidence: createdSignal.complaint_confidence,
                  classification: createdSignal.classification
                }}
                onViewSignal={handleViewSignal}
                onStartSimulation={handleStartSimulation}
                onFinalizeDraft={createdSignal.status === 'draft' ? handleFinalizeDraft : undefined}
                onDiscardDraft={createdSignal.status === 'draft' ? handleDiscardDraft : undefined}
                isLoading={isCreatingSimulation}
              />
            </div>
          )}

          {activeTab === 'simulations' ? (
            <>
              <div className="mb-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-border bg-card px-4 py-3">
                  <div className="text-2xl font-extrabold tracking-[-0.02em] text-foreground">{totalCases}</div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Total cases
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-card px-4 py-3">
                  <div className="text-2xl font-extrabold tracking-[-0.02em] text-foreground">{activeCases}</div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Active
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-card px-4 py-3">
                  <div className="text-2xl font-extrabold tracking-[-0.02em] text-foreground">{processingCases}</div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Processing
                  </div>
                </div>
                <div className="rounded-lg border border-[var(--go-bittersweet)]/35 bg-card px-4 py-3">
                  <div className="text-2xl font-extrabold tracking-[-0.02em] text-[var(--go-bittersweet)]">{urgentCases}</div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Past 8 weeks
                  </div>
                </div>
              </div>

              <div className="mb-5 flex items-center justify-between gap-4 rounded-lg border border-dashed border-border bg-card px-4 py-3">
                <div>
                  <div className="text-[13px] font-semibold text-foreground">Start from documents</div>
                  <div className="text-[12px] text-muted-foreground">
                    Paste complaint text or upload supporting files to create a new case.
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setIsWizardOpen(true)} className="gap-2">
                  <IconWrapper icon={FiUpload} className="h-4 w-4" />
                  Add complaint
                </Button>
              </div>

              {isLoading ? (
                <div className="rounded-lg border border-border bg-card px-6 py-12 text-center text-[13px] text-muted-foreground">
                  Loading cases...
                </div>
              ) : totalCases === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-card px-6 py-12 text-center">
                  <div className="text-[15px] font-bold text-foreground">No cases yet</div>
                  <p className="mt-2 text-[13px] text-muted-foreground">
                    Create your first case from a complaint or supporting documents.
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {sortedBuckets.map((bucketKey) => {
                    const bucketSimulations = groupedSimulations[bucketKey];

                    if (bucketSimulations.length === 0) {
                      return null;
                    }

                    return (
                      <section key={bucketKey}>
                        <div className="mb-3 flex items-center gap-2">
                          <h2 className="text-[14px] font-bold text-foreground">{getBucketLabel(bucketKey)}</h2>
                          <Badge variant="secondary">{bucketSimulations.length} cases</Badge>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          {bucketSimulations
                            .slice()
                            .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
                            .map((simulation) => (
                              <CaseCard
                                key={simulation.id}
                                simulation={simulation}
                                caseLabel={formatCaseLabel(simulation.id)}
                                lastActive={formatRelativeTime(simulation.updated_at)}
                                summary={
                                  simulation.signal_summary ||
                                  simulation.summary ||
                                  simulation.name ||
                                  'Complaint details not yet available.'
                                }
                                onOpen={handleSimulationClick}
                                onViewSignal={handleViewSignal}
                                onDelete={handleDelete}
                              />
                            ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="overflow-hidden rounded-lg bg-transparent">
              <FOSExplorerPage />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OmbudsmanWorkspace;
